// SEM-6349 — the org views' state, kept where an unmount cannot reach it.
//
// A name in the chart is a real address (`/people/:id`), so clicking one LEAVES
// the screen — and takes the open branches, the scroll offset and the diagram
// scale with it if they live in component state.
//
// sessionStorage, not the query string. The tab and the list filters are in the
// location (use-url-filters.ts) because they are short and worth sharing; the
// open-branch set is a list of employee ids that a real tenant runs into the
// thousands, which no URL should carry, and a scroll offset is not something
// anyone means to share. Session scope for the same reason store/tabs.ts and
// the tour's step index use it: it must survive a reload and a trip to a person,
// and start clean at the next sign-in.

const KEY_PREFIX = "novemai.org.view.";

/** Storage keys — one per independently-written piece, so a scroll tick never
 * re-serializes the (potentially huge) open-branch set. */
export const ORG_VIEW_KEYS = {
  chartExpanded: "chart.expanded",
  chartScrollTop: "chart.scroll-top",
  diagramExpanded: "diagram.expanded",
  diagramScale: "diagram.scale",
  diagramScroll: "diagram.scroll",
} as const;

export function readViewState<T>(key: string, revive: (raw: unknown) => T | null): T | null {
  try {
    const stored = window.sessionStorage.getItem(KEY_PREFIX + key);
    if (stored === null) return null;
    return revive(JSON.parse(stored));
  } catch {
    // Private-mode / quota-blocked storage, or a hand-edited value: the view
    // opens at its defaults rather than failing to render.
    return null;
  }
}

export function writeViewState(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(KEY_PREFIX + key, JSON.stringify(value));
  } catch {
    /* best effort — the in-memory state still holds for this page load */
  }
}

export function reviveIdList(raw: unknown): readonly string[] | null {
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : null;
}

export function reviveNumber(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

export interface ScrollOffset {
  readonly left: number;
  readonly top: number;
}

export function reviveScrollOffset(raw: unknown): ScrollOffset | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const left = reviveNumber(record["left"]);
  const top = reviveNumber(record["top"]);
  return left === null || top === null ? null : { left, top };
}
